<template>
  <SfDropdown
    trigger-size="small"
    :is-small="true"
    :dropdown-id="dropdownId"
    :is-open="isScopedDropdownOpen"
    variant="primary-inverted"
    @dropdown-trigger-clicked="handleToggleDropdown"
    @dropdown-closed="handleCloseDropdown"
  >
    <template #trigger>
      {{ dropdownLabel }}
    </template>

    <template #menu>
      <div
        class="sf-pad-2 remap-dropdown__container-menu"
        data-testid="remap-dropdown-content"
      >
        <div
          class="
            sf-below-1 sf-c-text sf-font-xs-heading sf-font-medium
            remap-dropdown__title
          "
        >
          {{ title }}
        </div>
        <div class="sf-below-1 sf-font-xs sf-c-text-darker">
          {{ subtitle }}
        </div>

        <div v-if="isScopedDropdownOpen" class="sf-mapping-list">
          <div class="remap-dropdown__search-row sf-below-2">
            <RemapDropdownSearch />

            <!-- sf-btn-bar owns the outer border, radius and overflow: hidden,
                 so the segments only need equal width and square corners. -->
            <div class="sf-btn-bar">
              <SfButtonDs
                v-if="props.items.length"
                :variant="showGroupVariant('ai')"
                class="!tw-flex-1 !tw-rounded-none"
                icon-before="wand-magic-sparkles"
                data-testid="show-ai-only-button"
                @click="setShow('ai')"
              >
                {{ showAiOnlyText }}
              </SfButtonDs>

              <SfButtonDs
                v-if="displayExternalButton"
                :variant="showGroupVariant('external')"
                class="!tw-flex-1 !tw-rounded-none"
                data-testid="show-external-only-button"
                @click="setShow('external')"
              >
                {{ showExternalOnlyText }}
              </SfButtonDs>

              <SfButtonDs
                :variant="showGroupVariant('all')"
                class="!tw-flex-1 !tw-rounded-none"
                data-testid="show-all-button"
                @click="setShow('all')"
              >
                {{ showAllText }}
              </SfButtonDs>
            </div>
          </div>
          <div
            class="
              sf-font-s sf-c-text-dark sf-font-medium sf-below-1
              remap-dropdown__subheader
            "
          >
            {{ chartOfAccountsText }}
          </div>
          <AccountGroupList
            v-scroll-into-view="{ behavior: 'smooth', block: 'nearest' }"
            @item-clicked="handleTreeItemClick"
          >
          </AccountGroupList>
        </div>
      </div>
    </template>
  </SfDropdown>
</template>
<script setup lang="ts">
import { dropdowns } from '@app/shared/types/dropdown'
import type { AccountId, AccountRange, Proposal } from '../shared-types'
import type { SearchState } from '../store/types'
import { MAPPING_SOURCES } from '../constants'
import { computed } from 'vue'

type ShowGroup = SearchState['remapDropdownShowGroup']

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import AccountGroupList from './account-group-list.vue'
import RemapDropdownSearch from './remap-dropdown-search.vue'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { useGroupList } from '@app/application/account-mapping/store/group-list'
import { useSearch } from '../store/search'
import scrollIntoView from '@app/shared/vue/directives/scrollIntoView'

defineOptions({
  name: 'RemapDropdown',
})

interface Props {
  accountId: AccountId
  accountRange: AccountRange
  items: Array<AccountRange>
  proposals: Array<Proposal>
  externallyMappedNumber?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: function () {
    return []
  },
  proposals: function () {
    return []
  },
  externallyMappedNumber: '',
})

const vScrollIntoView = scrollIntoView

const commonStore = useCommon()
const searchStore = useSearch()
const dropdownStore = useDropdown()
const groupListStore = useGroupList()

const dropdownId = dropdowns.ACCOUNT_MAPPING_TABLE_REMAP

const dropdownLabel = window.I18n.t('account_mapping_list.dropdown.remap_label')
const title = window.I18n.t('account_mapping_list.remap.title')
const subtitle = window.I18n.t('account_mapping_list.remap.subtitle')
const showAiOnlyText = window.I18n.t(
  'account_mapping_list.remap.ai_only_toggle'
)
const showExternalOnlyText = window.I18n.t(
  'account_mapping_list.remap.external_only_toggle'
)
const showAllText = window.I18n.t('account_mapping_list.remap.show_all_toggle')

const chartOfAccountsText = window.I18n.t(
  'account_mapping_list.silverfin_chart_of_accounts'
)

const isScopedDropdownOpen = computed(
  () =>
    dropdownStore.currentDropdownId === dropdownId &&
    groupListStore.focusedDropdown === props.accountId
)
const showGroupVariant = (group: ShowGroup): 'primary' | 'secondary' =>
  searchStore.remapDropdownShowGroup === group ? 'primary' : 'secondary'

const displayExternalButton = computed(() => {
  if (!props.externallyMappedNumber) {
    return false
  }

  return groupListStore.getExternallyMappedGroupIds.length > 0
})

function handleCloseDropdown() {
  groupListStore.persistDropdown('')
  dropdownStore.closeDropdown()
}

function handleToggleDropdown() {
  if (groupListStore.focusedDropdown === props.accountId) {
    groupListStore.persistDropdown('')
  } else {
    dropdownStore.closeDropdown() // close dropdown explicitly if we're clicking on another Remap button, otherwise the toggle below won't work
    groupListStore.persistDropdown(props.accountId)

    const aiGroupIds = dropdownAIGroupIds.value
    const externalGroupIds = groupListStore.getExternallyMappedGroupIds

    searchStore.setRemapDropdownAIFitleredGroupIds(aiGroupIds)
    searchStore.setRemapDropdownExternalFitleredGroupIds(externalGroupIds)

    if (props.proposals && props.proposals.length > 0) {
      const firstProposalSource = props.proposals[0].source

      if (firstProposalSource === MAPPING_SOURCES.EXTERNAL) {
        searchStore.setRemapDropdownShowGroup('external')
      } else if (firstProposalSource === MAPPING_SOURCES.AI) {
        searchStore.setRemapDropdownShowGroup('ai')
      } else {
        searchStore.setRemapDropdownShowGroup('all')
      }
    } else {
      searchStore.setRemapDropdownShowGroup('all')
    }
    commonStore.clearSearch()
    commonStore.focusRemapSearch()
  }

  dropdownStore.toggleDropdown(dropdownId)
}

const dropdownAIGroupIds = computed(() =>
  props.items.map((value) => commonStore.getAccountRangePerGroup[value])
)

function handleTreeItemClick({
  accountRange,
}: {
  id?: string
  accountRange: string
}) {
  if (accountRange === props.accountRange) {
    groupListStore.unmap({
      accountId: props.accountId,
      accountRange,
    })
  } else {
    groupListStore.remap({
      accountId: props.accountId,
      prevAccountRange: props.accountRange,
      accountRange,
    })
  }

  handleCloseDropdown()
}

function setShow(value: ShowGroup) {
  searchStore.setRemapDropdownShowGroup(value)
}
</script>
<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.remap-dropdown {
  &__container-menu {
    min-width: 600px;
  }

  &__search-row {
    display: flex;
    gap: space(1);
  }

  &__title {
    line-height: 1.5;
  }

  &__subtitle {
    line-height: 1.181;
  }

  &__subheader {
    line-height: 1.23;
  }
}
</style>
